import { X, Shield, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { isCleanText } from '@/utils/sanitize';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SuspensionConfig, SeverityLevel, TenantResponseDto } from '@/types/tenant.types';

// Validation schema for block tenant form
const blockTenantSchema = yup.object({
  custom_message: yup.string().required('Message is required').min(10, 'Message must be at least 10 characters')
    .test('clean-text', 'Please use only letters, numbers and standard punctuation', (v) => isCleanText(v)),
  contact_email: yup.string()
    .nullable()
    .optional()
    .test('valid-email', 'Must be a valid email address (e.g., user@example.com)', (value) => {
      // Allow empty/null values since field is optional
      if (!value || value.trim() === '') return true;
      // Strict email regex: local@domain.tld (requires TLD with 2+ chars)
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/;
      return emailRegex.test(value.trim());
    }),
  contact_link: yup.string()
    .nullable()
    .optional()
    .test('valid-url', 'Must be a valid URL starting with https:// or http://', (value) => {
      // Allow empty/null values since field is optional
      if (!value || value.trim() === '') return true;
      // Strict URL regex: must start with http:// or https:// and have valid domain
      const urlRegex = /^https?:\/\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?$/;
      return urlRegex.test(value.trim());
    }),
  severity: yup.string().oneOf(['info', 'warning', 'critical']).required(),
  show_organization_details: yup.boolean().required(),
  auto_unblock_at: yup.string()
    .nullable()
    .optional()
    .test('is-future-date', 'Must select a future date and time', function(value) {
      if (!value) return true;
      const selectedDate = new Date(value);
      return selectedDate > new Date();
    })
});

interface BlockTenantModalProps {
  isOpen: boolean;
  tenant: TenantResponseDto | null;
  onClose: () => void;
  onConfirm: (suspensionConfig: SuspensionConfig) => void;
  isLoading?: boolean;
}

export default function BlockTenantModal({
  isOpen,
  tenant,
  onClose,
  onConfirm,
  isLoading = false
}: BlockTenantModalProps) {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    resolver: yupResolver(blockTenantSchema),
    defaultValues: {
      custom_message: 'This organization is temporarily unavailable due to scheduled maintenance.',
      contact_email: '',
      contact_link: '',
      severity: 'warning' as SeverityLevel,
      show_organization_details: true,
      auto_unblock_at: ''
    }
  });

  const watchedAutoUnblockAt = watch('auto_unblock_at');
  const watchedSeverity = watch('severity');

  if (!isOpen || !tenant) return null;

  const calculateSecondsRemaining = (): number | undefined => {
    if (!watchedAutoUnblockAt) return undefined;

    const now = new Date();
    const unblockDate = new Date(watchedAutoUnblockAt);
    const diffMs = unblockDate.getTime() - now.getTime();

    if (diffMs <= 0) return undefined; // Don't show preview for past dates
    return Math.floor(diffMs / 1000); // Convert ms to seconds
  };

  const onFormSubmit = (data: any) => {
    const secondsRemaining = calculateSecondsRemaining();
    const config: SuspensionConfig = {
      custom_message: data.custom_message,
      contact_email: data.contact_email?.trim() || undefined,
      contact_link: data.contact_link?.trim() || undefined,
      severity: data.severity,
      show_organization_details: data.show_organization_details,
      seconds_remaining: secondsRemaining,
      blocked_at: new Date(),
      blocked_by: 'current_admin' // Will be set by backend from context
    };

    onConfirm(config);
  };

  const getSeverityIcon = (sev: SeverityLevel) => {
    switch (sev) {
      case 'info':
        return <Info className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'critical':
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getSeverityColor = (sev: SeverityLevel) => {
    switch (sev) {
      case 'info':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'warning':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-300';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Block Tenant</h2>
                <p className="text-sm text-gray-500">{tenant.name}</p>
                <code className="text-xs text-gray-400 font-mono">{tenant.tenant_key}</code>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Custom Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message to Users <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('custom_message')}
                placeholder="Enter the message users will see when trying to access this tenant..."
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm resize-none ${
                  errors.custom_message ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.custom_message && (
                <p className="text-xs text-red-600 mt-1">{errors.custom_message.message}</p>
              )}
              {!errors.custom_message && (
                <p className="text-xs text-gray-500 mt-1">This message will be displayed prominently on the blocked page</p>
              )}
            </div>

            {/* Severity Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Severity Level <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['info', 'warning', 'critical'] as SeverityLevel[]).map((sev) => (
                  <button
                    key={sev}
                    type="button"
                    onClick={() => setValue('severity', sev)}
                    disabled={isLoading}
                    className={`
                      flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all
                      ${watchedSeverity === sev ? getSeverityColor(sev) + ' border-current' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {getSeverityIcon(sev)}
                    <span className="text-xs font-semibold capitalize">{sev}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email (Optional)
                </label>
                <input
                  type="text"
                  {...register('contact_email')}
                  placeholder="support@company.com"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${
                    errors.contact_email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading}
                />
                {errors.contact_email && (
                  <p className="text-xs text-red-600 mt-1">{errors.contact_email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Support Link (Optional)
                </label>
                <input
                  type="text"
                  {...register('contact_link')}
                  placeholder="https://support.company.com"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${
                    errors.contact_link ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isLoading}
                />
                {errors.contact_link && (
                  <p className="text-xs text-red-600 mt-1">{errors.contact_link.message}</p>
                )}
              </div>
            </div>

            {/* Auto-Unblock Configuration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto-Unblock Date & Time (Optional)
              </label>
              <input
                type="datetime-local"
                {...register('auto_unblock_at')}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm ${
                  errors.auto_unblock_at ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.auto_unblock_at && (
                <p className="text-xs text-red-600 mt-1">{errors.auto_unblock_at.message}</p>
              )}
              {!errors.auto_unblock_at && (
                <p className="text-xs text-gray-500 mt-1">Leave empty to keep blocked indefinitely until manually unblocked</p>
              )}

              {/* Expiry Preview */}
              {calculateSecondsRemaining() && (
                <div className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2 mt-2 space-y-1">
                  <div>
                    <strong>Will unblock in:</strong> {(() => {
                      const seconds = calculateSecondsRemaining()!;
                      const hours = Math.floor(seconds / 3600);
                      const minutes = Math.floor((seconds % 3600) / 60);
                      if (hours > 24) {
                        const days = Math.floor(hours / 24);
                        return `${days} day${days > 1 ? 's' : ''} ${hours % 24}h`;
                      }
                      return `${hours}h ${minutes}m`;
                    })()}
                  </div>
                  <div>
                    <strong>Unblock date:</strong> {(() => {
                      const seconds = calculateSecondsRemaining()!;
                      const unblockDate = new Date(Date.now() + seconds * 1000);
                      return unblockDate.toLocaleString();
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Show Organization Details */}
            <div>
              <label className="flex items-center gap-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  {...register('show_organization_details')}
                  className="w-4 h-4 text-red-600 focus:ring-red-500 rounded"
                  disabled={isLoading}
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 block">
                    Show organization details on blocked page
                  </span>
                  <span className="text-xs text-gray-500">
                    Displays tenant key and status information
                  </span>
                </div>
              </label>
            </div>

            {/* Warning Box */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <strong>Important:</strong> Blocking this tenant will immediately prevent all users from logging in.
                  The tenant status will be changed to <strong>SUSPENDED</strong>.
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit(onFormSubmit)}
              disabled={isLoading}
              className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Blocking...</span>
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  <span>Block Tenant</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
